"use client";
import React, { useEffect, useRef, useState } from "react";
import { getStrapiMedia, getStrapiURL } from "../lib/api-helpers";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import gsap from "gsap";
import Image from "next/image";

gsap.registerPlugin(ScrollTrigger);

interface items {
  title: string;
  icon: {
    data: {
      attributes: {
        name: string;
        url: string;
      };
    };
  };
  borderColor: string;
  backgroundColor: string;
  description: string;
  hasFourCards: boolean;
}

interface Cards {
  data: {
    headingCard: string;
    isArc: boolean;
    items: items[];
  };
}

let max4cards: boolean = false;

function Card({ title, icon, borderColor, backgroundColor, description, hasFourCards }: items) {
  const imgUrl = getStrapiMedia(icon.data?.attributes?.url);
  const [vPadding, setVpadding] = useState('py-[48px]');
  const [fontWeight, setFontWeight] = useState('font-normal');
  useEffect(() => {
    if(hasFourCards){
      const verticalPadding = 'pt-[48px] pb-[24px]'; 
      setVpadding(verticalPadding);
    } else {
      const fontWeight = title ? 'font-normal' : 'font-medium'
      setFontWeight(fontWeight);
    }
  }, []);

  return (
    <div
      style={{backgroundColor, borderColor}}
      className={`container mx-auto rounded-[18px] lg:rounded-[32px] border-[2px] border-[#DBF2FF]  flex flex-col items-center justify-start w-full px-[24px] ${vPadding}`}>
      <div className="flex items-center justify-center mb-[24px]">
        <Image
          className={`w-24 min-w-24 h-24 lg:w-20 lg:min-w-20 lg:h-20 object-contain`}
          src={imgUrl ?? ""}
          alt={icon.data?.attributes?.name}
          width={40}
          height={40}
        />
      </div>
      <section>
        <h2 className='sr-only'>Card Section</h2>
        {
          title &&
          <div
            className={`text-[#001630] text-[24px] lg:text-xl xl:text-2xl font-semibold text-center mb-[8px]`}
            dangerouslySetInnerHTML={{__html: title}}
          /> 
        }  
        <div
          className={`border-card-description relative title-font text-2xl md:text-xl lg:text-lg text-[#001630] text-center ${fontWeight}`}
          dangerouslySetInnerHTML={{__html:description}}
        />
      </section>
    </div>
  );
}

const CardSection = ({ data }: Cards) => {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    gsap.fromTo(
      cardRefs.current,
      { y: 100 },
      {
        y: 0,
        stagger: 0.25,
        duration: 0.8,

        scrollTrigger: {
          trigger: cardRefs.current,
          toggleActions: "restart none none reverse",
          start: "top 70%",
        },
      }
    );
    max4cards = data.items.length == 4;
  }, []);

  return (
    <div
      style={{backgroundColor: data.isArc? '#ffffffff' : '#DBF2FF4D'}}
      className={`relative flex flex-col items-center justify-center px-[24px] py-[80px] md:py-[96px] overflow-hidden height-100`}
    >
      <div className="container flex flex-col items-center justify-center gap-5 mx-auto">
        <div className="title-font !leading-[1.2] text-5xl 2xl:text-6xl text-center sm:w-10/12 xl:w-9/12 2xl:w-11/12 font-bold text-[#032d60] mb-[36px] lg:mb-[60px]" 
            dangerouslySetInnerHTML={{__html:data.headingCard}} />
        <section className="relative w-full xl:max-w-[1400px]">
          <h2 className='sr-only'>Card Section</h2>
          {data.isArc &&  <div className="bg-[#75CCFF80] absolute h-3/4 w-full -bottom-[40px] rounded-[40px] hidden lg:block"></div>}
          <div
            className={`grid grid-cols-1 content-center lg:px-8 relative z-1  ${
              data.items.length == 4
                ? "lg:grid-cols-4 gap-[24px]"
                : "lg:grid-cols-3 gap-[24px]"
            } sm:grid-cols-2 `}
          >
            {data.items.map((item, index) => {
              return (
                <div
                  className="flex h-full"
                  ref={(el) => { cardRefs.current[index] = el; }}
                  key={index}
                >
                  <Card key={index} {...item} hasFourCards={data.items.length == 4}/>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default CardSection;
